/**
 * 商談詳細「同意書」タブ (Phase 3c)。
 *
 * 主軸カテゴリーの requiresConsent=true のときのみ表示される。
 * ただし同じ顧客が同じ主軸カテゴリーの別の商談でサイン済みなら表示しない (useDealConsentRequirement)。
 *
 * 機能:
 *   - 同意書送付ボタン: 紐付くテンプレを選んで公開リンク生成＋メール送信
 *   - 送付済み公開リンク一覧 (再送用に URL コピー可)
 *   - 提出済み同意書プレビュー (サイン済み確認)
 */
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useConsentDocuments,
  useConsentPublicLinksByDeal,
  useConsentSubmissionsByDeal,
  useIssueConsentPublicLink,
} from '@/hooks/useConsentDocuments';
import { type DealRecord } from '@/hooks/useDeals';

interface Props {
  deal: DealRecord;
}

function fmtDate(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      return (d as { toDate: () => Date }).toDate().toLocaleString('ja-JP');
    } catch {
      return '';
    }
  }
  if (typeof d === 'string') return d;
  return '';
}

export function DealConsentTab({ deal }: Props) {
  const allDocs = useConsentDocuments();
  const links = useConsentPublicLinksByDeal(deal.id);
  const submissions = useConsentSubmissionsByDeal(deal.id);
  const issue = useIssueConsentPublicLink();

  // 主軸カテゴリーで絞り込み (一致するものを先頭、未設定は後ろ)
  const sortedDocs = useMemo(() => {
    const all = allDocs.data ?? [];
    const matched = all.filter(
      (d) => d.primaryCategoryId === deal.primaryCategoryId && d.status === 'published',
    );
    const other = all.filter(
      (d) => d.primaryCategoryId !== deal.primaryCategoryId && d.status === 'published',
    );
    return [...matched, ...other];
  }, [allDocs.data, deal.primaryCategoryId]);

  const [selectedDocId, setSelectedDocId] = useState<string>('');

  const handleSend = async () => {
    if (!selectedDocId) {
      toast.error('送付する同意書を選択してください');
      return;
    }
    if (!deal.contactId) {
      toast.error('商談に顧客が紐付いていません');
      return;
    }
    try {
      const r = await issue.mutateAsync({
        dealId: deal.id,
        consentDocumentId: selectedDocId,
      });
      if (r.mailSent) {
        toast.success(`公開リンクを発行＋メール送信しました`);
      } else {
        toast.success(`公開リンクを発行しました (メール送信は失敗: ${r.mailReason ?? '不明'}）`);
      }
    } catch (e) {
      toast.error(`送付失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const isLoading = links.isLoading || submissions.isLoading || allDocs.isLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const linkList = links.data ?? [];
  const submissionList = submissions.data ?? [];

  return (
    <div className="space-y-4">
      {/* 送付セクション */}
      <section className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4">
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
          <ShieldCheck className="size-4" />
          同意書を送付
        </h2>
        {sortedDocs.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="公開状態の同意書テンプレがありません"
            description="同意書管理画面で「公開」状態にしてから送付できます。"
          />
        ) : (
          <div className="space-y-2">
            <div>
              <Label className="text-xs">テンプレート選択</Label>
              <select
                value={selectedDocId}
                onChange={(e) => setSelectedDocId(e.target.value)}
                className="w-full px-2 py-1.5 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-sm"
              >
                <option value="">選択してください…</option>
                {sortedDocs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                    {d.primaryCategoryId === deal.primaryCategoryId ? ' ★主軸カテゴリー一致' : ''}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleSend()}
              disabled={issue.isPending || !selectedDocId}
              className="w-full"
            >
              {issue.isPending ? (
                <Loader2 className="size-4 mr-1 animate-spin" />
              ) : (
                <Mail className="size-4 mr-1" />
              )}
              公開リンク発行 + メール送信
            </Button>
          </div>
        )}
      </section>

      {/* 送付済み公開リンク */}
      <section>
        <h2 className="text-sm font-semibold mb-2">送付履歴</h2>
        {linkList.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] italic px-1">
            まだ送付された同意書はありません。
          </p>
        ) : (
          <div className="space-y-2">
            {linkList.map((link) => {
              const matched = submissionList.find((s) => s.publicLinkId === link.id);
              const signed = matched || link.submittedAt;
              const publicUrl = `${window.location.origin}/consent/${link.dealId}/${link.id}`;
              return (
                <ConsentLinkCard
                  key={link.id}
                  linkId={link.id}
                  title={link.documentSnapshot.title}
                  issuedAt={fmtDate(link.issuedAt)}
                  signed={Boolean(signed)}
                  signatureName={matched?.signatureName ?? null}
                  signatureDate={matched?.signatureDate ?? null}
                  audit={matched?.audit ?? null}
                  publicUrl={publicUrl}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────
// 公開リンクカード (折りたたみ式)
// ─────────────────────────────────────────────

interface AuditData {
  userAgent?: string | null;
  ipAddress?: string | null;
  timezone?: string | null;
  language?: string | null;
  screenResolution?: string | null;
  viewportSize?: string | null;
  platform?: string | null;
  referrer?: string | null;
  clientSubmittedAt?: string | null;
  pageLoadDurationMs?: number | null;
}

function ConsentLinkCard({
  linkId,
  title,
  issuedAt,
  signed,
  signatureName,
  signatureDate,
  audit,
  publicUrl,
}: {
  linkId: string;
  title: string;
  issuedAt: string;
  signed: boolean;
  signatureName: string | null;
  signatureDate: string | null;
  audit: AuditData | null;
  publicUrl: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg bg-[var(--bg-elevated)] p-3 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm truncate">{title}</span>
        {signed ? (
          <Badge variant="approved">
            <CheckCircle className="size-3 mr-1" />
            サイン済み
          </Badge>
        ) : (
          <Badge variant="draft">
            <Clock className="size-3 mr-1" />
            未提出
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-[var(--text-muted)]">
        送付: {issuedAt}
        {signed && signatureName && ` ／ サイン: ${signatureName} (${signatureDate ?? ''})`}
      </p>
      <div className="flex gap-1">
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          onClick={() => {
            navigator.clipboard.writeText(publicUrl);
            toast.success('公開URLをコピーしました');
          }}
        >
          <Copy className="size-3.5 mr-1" />
          URL コピー
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          onClick={() => window.open(publicUrl, '_blank')}
        >
          {signed ? (
            <FileText className="size-3.5 mr-1" />
          ) : (
            <ExternalLink className="size-3.5 mr-1" />
          )}
          {signed ? 'サイン済み同意書' : 'プレビュー'}
        </Button>
      </div>

      {signed && audit && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1 text-[11px] text-[var(--text-muted)] hover:text-[var(--accent)] flex items-center gap-1"
          >
            {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            証跡メタデータを {open ? '隠す' : '表示'}
          </button>
          {open && <AuditDetails audit={audit} linkId={linkId} />}
        </>
      )}
    </div>
  );
}

function AuditDetails({ audit, linkId }: { audit: AuditData; linkId: string }) {
  const rows: Array<[string, string | null | undefined]> = [
    ['リンク ID', linkId],
    ['IP アドレス', audit.ipAddress],
    ['User-Agent', audit.userAgent],
    ['タイムゾーン', audit.timezone],
    ['言語', audit.language],
    ['プラットフォーム', audit.platform],
    ['画面解像度', audit.screenResolution],
    ['ビューポート', audit.viewportSize],
    ['リファラ', audit.referrer],
    ['クライアント送信時刻', audit.clientSubmittedAt],
    [
      'ページ滞在時間 (秒)',
      audit.pageLoadDurationMs != null ? String(Math.round(audit.pageLoadDurationMs / 1000)) : null,
    ],
  ];

  return (
    <div className="mt-2 rounded bg-[var(--bg-surface)] p-2 space-y-1">
      <p className="text-[10px] text-[var(--text-muted)] mb-1">
        ※ 提出時にブラウザから取得した監査情報。法的証跡として保持。
      </p>
      {rows.map(([label, value]) => {
        if (!value) return null;
        return (
          <div key={label} className="flex items-start gap-2 text-[11px] leading-snug">
            <span className="text-[var(--text-muted)] flex-shrink-0 w-32">{label}</span>
            <span className="flex-1 break-all font-mono">{value}</span>
          </div>
        );
      })}
    </div>
  );
}
